/**
 * Personenkonten: every supplier and customer gets an account number of its own, so the
 * accountant keeps open items per partner and the ZM stays complete.
 *
 * A partner is recognised by its UID, or failing that by its name folded the way `match.ts`
 * folds statement lines, so "Bürobedarf Wien GmbH" and "BUEROBEDARF WIEN GMBH" are one partner.
 * Numbers are handed out once - the next free one in the role's range - and stored in
 * `partner_accounts`; an existing number is never changed or reused.
 */
import type { DocumentRecord } from "@/lib/api/documents";
import type { PartnerAccountsResponse } from "@/lib/platform/pb-types";
import type { ExportSettings } from "./settings";

export type PartnerAccount = Pick<
  PartnerAccountsResponse,
  "role" | "key" | "number" | "name" | "vatId" | "iban" | "address"
>;
export type PartnerRole = PartnerAccount["role"];

/** Suppliers send ER, customers receive AR; a Barbeleg is booked on the cash account instead. */
export function partnerRole(document: Pick<DocumentRecord, "kind">): PartnerRole | null {
  if (document.kind === "ER") return "supplier";
  if (document.kind === "AR") return "customer";
  return null;
}

export function partnerKey(
  document: Pick<DocumentRecord, "partnerVatId" | "partnerName">,
): string | null {
  const vatId = (document.partnerVatId ?? "").replace(/\s+/g, "").toUpperCase();
  if (vatId) return `uid:${vatId}`;
  const name = (document.partnerName ?? "")
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
  return name ? `name:${name}` : null;
}

export type PartnerPlan = {
  /** Account number per document id, for every ER and AR with a recognisable partner. */
  numbers: Map<string, number>;
  /** Partners seen for the first time, with the number they get. */
  toCreate: PartnerAccount[];
};

export function planPartnerAccounts(
  documents: DocumentRecord[],
  existing: PartnerAccount[],
  settings: Pick<ExportSettings, "supplierFrom" | "customerFrom">,
): PartnerPlan {
  const known = new Map(existing.map((account) => [`${account.role}|${account.key}`, account]));
  const taken = new Set(existing.map((account) => account.number));
  const next: Record<PartnerRole, number> = {
    supplier: settings.supplierFrom,
    customer: settings.customerFrom,
  };
  const numbers = new Map<string, number>();
  const toCreate: PartnerAccount[] = [];

  for (const document of documents) {
    const role = partnerRole(document);
    const key = partnerKey(document);
    if (!role || !key) continue;
    let account = known.get(`${role}|${key}`);
    if (!account) {
      while (taken.has(next[role])) next[role] += 1;
      account = {
        role,
        key,
        number: next[role],
        name: document.partnerName ?? "",
        vatId: document.partnerVatId ?? "",
        iban: document.partnerIban ?? "",
        address: document.partnerAddress ?? "",
      };
      taken.add(account.number);
      known.set(`${role}|${key}`, account);
      toCreate.push(account);
    }
    numbers.set(document.id, account.number);
  }
  return { numbers, toCreate };
}
